import { useEffect } from 'react';
import { trackEvent } from '../utils/analytics';

/** Depths reported, as a percentage of the page's full height. */
export const SCROLL_DEPTHS = [25, 50, 75, 100] as const;

/** "Near the top", in pixels: the page has settled and reading can begin. */
const SETTLED_AT_TOP = 4;

/**
 * Report how far down the page a visitor has read: one GA4 event per depth,
 * each at most once per page view, with `{ percent }` as the only parameter.
 *
 * Depth is the bottom edge of the viewport as a share of the page height, so
 * 100 means the very end of the page. A jump past several depths reports each
 * of them. The listener is passive and does at most one layout read per animation
 * frame (nothing is written), so it cannot thrash layout, and it removes itself
 * once every depth has been reported.
 *
 * Nothing counts until the page has settled at the top. An in-app arrival from a
 * deeply scrolled page (a footer link, Back) starts at the old scroll position and
 * `ScrollToTop` then smooth-scrolls up, so a reading taken on the way would report a
 * read of a page the visitor has not seen. The hook arms on the first reading near
 * the top, which every real arrival reaches, and counts from there.
 */
export function useScrollDepth(eventName: string): void {
  useEffect(() => {
    const reported = new Set<number>();
    let frame = 0;
    let scheduled = false;
    // Arrived at the top (the normal case), or still travelling there from a deeper page.
    let settled = window.scrollY <= SETTLED_AT_TOP;

    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      frame = window.requestAnimationFrame(check);
    };

    function check() {
      scheduled = false;
      if (!settled) {
        if (window.scrollY > SETTLED_AT_TOP) return;
        settled = true;
      }
      const total = document.documentElement.scrollHeight;
      if (total <= 0) return;
      // Two pixels of slack: scrollY is fractional on some displays and never quite reaches the end.
      const percent = ((window.scrollY + window.innerHeight + 2) / total) * 100;
      for (const depth of SCROLL_DEPTHS) {
        if (percent >= depth && !reported.has(depth)) {
          reported.add(depth);
          trackEvent(eventName, { percent: depth });
        }
      }
      if (reported.size === SCROLL_DEPTHS.length) window.removeEventListener('scroll', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    // A very tall viewport may already show a quarter of the page.
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scheduled) window.cancelAnimationFrame(frame);
    };
  }, [eventName]);
}
